import {
  Calendar, Clock 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { cn } from '@/lib/classnames';
import type {
  BatchPlan, BatchPlanStatus 
} from '@/features/production';

const BATCH_STATUS_COLORS: Record<BatchPlanStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  scheduled: 'bg-blue-100 text-blue-800',
  'in-progress': 'bg-amber-100 text-amber-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const BATCH_STATUS_LABELS: Record<BatchPlanStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  'in-progress': 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export function BatchPlanCard({ batchPlan }: { readonly batchPlan: BatchPlan }) {
  const statusColor = BATCH_STATUS_COLORS[batchPlan.status];
  const statusLabel = BATCH_STATUS_LABELS[batchPlan.status];
  const progress = batchPlan.totalItems > 0
    ? Math.round((batchPlan.completedItems / batchPlan.totalItems) * 100)
    : 0;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base">{batchPlan.name}</CardTitle>
            <p className="text-sm text-muted-foreground">{batchPlan.venueName}</p>
          </div>
          <span className={cn('rounded-full px-2 py-1 text-xs font-medium', statusColor)}>
            {statusLabel}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            {new Date(batchPlan.date).toLocaleDateString()}
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            {batchPlan.startTime} - {batchPlan.endTime}
          </div>
        </div>
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span>Progress</span>
            <span className="font-medium">{progress}%</span>
          </div>
          <div className="h-2 rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {batchPlan.completedItems} of {batchPlan.totalItems} items
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
